import { useEffect, useState } from "react";
import { Field, inputCls } from "@/components/Shared";
import DepartmentPicker from "@/components/DepartmentPicker";
import CountryPicker from "@/components/CountryPicker";
import CityPicker from "@/components/CityPicker";
import { api } from "@/lib/api";
import {
  X, ChevronLeft, ChevronRight, Check, User, Briefcase, FileText,
  ChevronDown,
} from "lucide-react";
import { toast } from "sonner";

export const EMPLOYEE_EMPTY = {
  // Stammdaten
  employee_number: "", first_name: "", last_name: "", sv_number: "", health_insurance: "",
  cost_center: "", phone: "", private_email: "", email: "", birth_date: "", birth_place: "",
  time_tracking_workplace: "", subcontractor: "", timesheet_number: "", continental_vdo_id: "",
  saturday_in_tracking: false,
  // Adresse
  address: "", address_extra: "", country: "AT", zip_code: "", city: "",
  bank_name: "", iban: "", bic: "",
  // Führerschein
  driver_license_number: "", driver_license_authority: "", driver_license_groups: "B", driver_license_expiry: "",
  // Identitätsdokumente
  identity_doc_type: "reisepass", identity_doc_number: "",
  identity_doc_issue_date: "", identity_doc_valid_until: "", identity_doc_authority: "",
  // Staatsbürgerschaft / Aufenthalt
  citizenship: "AT", residence_doc_type: "", work_permit: "", residence_authority: "",
  residence_doc_number: "", residence_issue_date: "", residence_valid_until: "",
  work_permit_valid_until: "",
  // Anstellung
  department: "", department_id: "", firm_id: "", role: "fahrer", status: "aktiv",
  carrier: "",
  employment_start: "", employment_end: "", employment_change_date: "",
  contribution_account_number: "", create_documents: true, checklist_id: "",
  overtime_calc_by_days: false, daily_normal_hours: 8, weekly_hours: 40,
  vacation_calc_from_start: true, default_workplace: "",
  // Lohn
  calc_total_salary: true, driving_log_type: "", driving_log_by_vehicle_type: false, certificate: "",
  payroll_rule_group_id: "", salary_type: "hourly", payroll_number: "", payroll_notes: "",
  contracted_weekly_hours: 40, contracted_daily_hours: 8,
  overtime_eligible: true, tagesgeld_eligible: true,
  saturday_bonus_eligible: true, sunday_bonus_eligible: true, holiday_bonus_eligible: true,
  // Sonstiges
  notes: "", emergency_contact_name: "", emergency_contact_phone: "", emergency_contact_relation: "",
  // Onboarding-Rebuild fields
  kv_category: "",
  working_hour_model: "",
  protokollnummer: "",
  position: "",
  first_working_date: "",
  vacation_start_date: "",
  is_eu_citizen: true,
  visa_type: "", visa_number: "", visa_valid_until: "",
  _enable_portal_on_create: true,
  _portal_username: "",
};

// Wizard now uses 3 clear steps instead of 5 — same data model, less noise.
export const EMPLOYEE_STEPS = ["Person", "Anstellung & Lohn", "Dokumente & Portal"];

const EU_EEA_PLUS_CH = new Set([
  "AT","BE","BG","HR","CY","CZ","DK","EE","FI","FR","DE","GR","HU","IE","IT","LV","LT","LU","MT","NL",
  "PL","PT","RO","SK","SI","ES","SE",
  "IS","LI","NO",
  "CH",
]);
const needsResidenceDocs = (citizenship) => !!citizenship && !EU_EEA_PLUS_CH.has(citizenship);

const STEP_META = [
  { icon: User,      title: "Person",            desc: "Name, Kontaktdaten, Adresse & Bank" },
  { icon: Briefcase, title: "Anstellung & Lohn", desc: "Vertragsdaten, KV-Kategorie & Regelgruppe" },
  { icon: FileText,  title: "Dokumente & Portal",desc: "Führerschein, Ausweis, Aufenthalt & Fahrer-Portal" },
];

export default function EmployeeFormWizard({ form, setForm, onClose, onSave, editing }) {
  const [step, setStep] = useState(0);
  const [ruleGroups, setRuleGroups] = useState([]);
  const f = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    api.get("/payroll/rule-groups").then(({ data }) => setRuleGroups(data.groups || [])).catch(() => {});
  }, []);

  const canProceed =
    step === 0
      ? form.first_name.trim() && form.last_name.trim() && (form.address || "").trim()
      : true;

  function next() {
    if (!canProceed) { toast.error("Bitte Pflichtfelder ausfüllen"); return; }
    if (step < EMPLOYEE_STEPS.length - 1) setStep(step + 1);
    else onSave();
  }

  const allAtOnce = editing;
  const StepIcon = STEP_META[step].icon;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50" data-testid="employee-form-modal">
      <div className="bg-zinc-950 border border-zinc-800 rounded-lg w-full max-w-3xl max-h-[94vh] overflow-hidden flex flex-col shadow-2xl">
        {/* Header */}
        <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between bg-zinc-900/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-600/15 border border-blue-600/30 flex items-center justify-center">
              <StepIcon className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                {editing ? "Mitarbeiter bearbeiten" : "Neuer Mitarbeiter"}
              </div>
              <h3 className="font-display text-base font-semibold text-zinc-50 mt-0.5">
                {allAtOnce ? "Alle Felder" : STEP_META[step].title}
              </h3>
              {!allAtOnce && (
                <div className="text-[11px] text-zinc-500 mt-0.5">{STEP_META[step].desc}</div>
              )}
            </div>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-100 p-2 rounded hover:bg-zinc-800" data-testid="employee-form-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Progress */}
        {!allAtOnce && (
          <div className="px-6 pt-4 pb-3 border-b border-zinc-800 bg-zinc-900/30">
            <ProgressBar step={step} onJump={(i) => i <= step && setStep(i)} />
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {(allAtOnce || step === 0) && <StepPerson form={form} setForm={setForm} f={f} />}
          {(allAtOnce || step === 1) && <StepEmployment form={form} setForm={setForm} f={f} ruleGroups={ruleGroups} />}
          {(allAtOnce || step === 2) && <StepDocsPortal form={form} setForm={setForm} f={f} editing={editing} />}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-zinc-800 bg-zinc-900/50 flex justify-between gap-2">
          <div>
            {!allAtOnce && step > 0 && (
              <button onClick={() => setStep(step - 1)} className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-zinc-800 hover:bg-zinc-700 rounded text-zinc-200" data-testid="emp-back-btn">
                <ChevronLeft className="w-4 h-4" /> Zurück
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm bg-zinc-800 hover:bg-zinc-700 text-zinc-100 rounded">Abbrechen</button>
            {allAtOnce ? (
              <button onClick={onSave} data-testid="emp-save-btn" className="px-5 py-2 text-sm bg-blue-600 hover:bg-blue-500 text-white rounded font-medium">Speichern</button>
            ) : (
              <button
                onClick={next}
                data-testid={step === EMPLOYEE_STEPS.length - 1 ? "emp-save-btn" : "emp-next-btn"}
                disabled={!canProceed}
                className="inline-flex items-center gap-1.5 px-5 py-2 text-sm bg-blue-600 hover:bg-blue-500 disabled:bg-zinc-800 disabled:text-zinc-500 text-white rounded font-medium"
              >
                {step === EMPLOYEE_STEPS.length - 1 ? "Mitarbeiter anlegen" : <>Weiter <ChevronRight className="w-4 h-4" /></>}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Progress Bar ─────────────────────────────────────────────────
function ProgressBar({ step, onJump }) {
  return (
    <div className="flex items-center gap-3" data-testid="emp-progress-bar">
      {STEP_META.map((m, i) => {
        const isActive = i === step;
        const isDone = i < step;
        const clickable = i <= step;
        return (
          <div key={m.title} className="flex items-center gap-3 flex-1">
            <button
              onClick={() => clickable && onJump(i)}
              disabled={!clickable}
              data-testid={`emp-progress-step-${i}`}
              className={`flex items-center gap-2 ${clickable ? "cursor-pointer" : "cursor-default"}`}
            >
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border transition-colors ${
                isActive ? "bg-blue-600 border-blue-500 text-white shadow-lg shadow-blue-600/30" :
                isDone ? "bg-emerald-600/20 border-emerald-500/50 text-emerald-300" :
                "bg-zinc-900 border-zinc-700 text-zinc-500"
              }`}>
                {isDone ? <Check className="w-3.5 h-3.5" /> : i + 1}
              </div>
              <span className={`text-xs font-medium hidden sm:inline ${
                isActive ? "text-zinc-100" : isDone ? "text-zinc-300" : "text-zinc-500"
              }`}>{m.title}</span>
            </button>
            {i < STEP_META.length - 1 && (
              <div className={`flex-1 h-0.5 rounded ${isDone ? "bg-emerald-500/50" : "bg-zinc-800"}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Disclosure Section (Erweitert-Toggle) ────────────────────────
function Advanced({ title = "Erweiterte Felder", children, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-4 border border-dashed border-zinc-800 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        data-testid={testid}
        className="w-full px-4 py-2.5 flex items-center justify-between text-xs font-medium text-zinc-300 hover:bg-zinc-900/50"
      >
        <span className="flex items-center gap-2">
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
          {title}
        </span>
        <span className="text-[10px] text-zinc-500 uppercase tracking-wider">
          {open ? "Verbergen" : "Anzeigen"}
        </span>
      </button>
      {open && (
        <div className="p-4 border-t border-zinc-800 grid grid-cols-1 sm:grid-cols-2 gap-4 bg-zinc-950/50">
          {children}
        </div>
      )}
    </div>
  );
}

function Group({ title, children }) {
  return (
    <div className="mb-5 last:mb-0">
      <h4 className="text-xs font-semibold text-zinc-300 uppercase tracking-wider mb-3 border-l-2 border-blue-500 pl-2">{title}</h4>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>
    </div>
  );
}

// ─── Step 1: Person ───────────────────────────────────────────────
function StepPerson({ form, setForm, f }) {
  return (
    <div>
      <Group title="Name & Geburt">
        <Field label="Vorname" required>
          <input value={form.first_name} onChange={f("first_name")} data-testid="emp-field-first_name"
            className={`${inputCls} ${!form.first_name ? "border-red-500/40" : ""}`} />
        </Field>
        <Field label="Nachname" required>
          <input value={form.last_name} onChange={f("last_name")} data-testid="emp-field-last_name"
            className={`${inputCls} ${!form.last_name ? "border-red-500/40" : ""}`} />
        </Field>
        <Field label="SV-Nummer (10-stellig)">
          <input value={form.sv_number || ""} onChange={f("sv_number")} maxLength={10} placeholder="1234010180" data-testid="emp-field-sv_number" className={inputCls} />
        </Field>
        <Field label="Geburtsdatum">
          <input type="date" value={form.birth_date || ""} onChange={f("birth_date")} data-testid="emp-field-birth_date" className={inputCls} />
        </Field>
      </Group>

      <Group title="Kontakt">
        <Field label="Mobilnummer">
          <input value={form.phone || ""} onChange={f("phone")} placeholder="+43 …" data-testid="emp-field-phone" className={inputCls} />
        </Field>
        <Field label="Persönliche E-Mail">
          <input type="email" value={form.private_email || ""} onChange={f("private_email")} data-testid="emp-field-private_email" className={inputCls} />
        </Field>
        <Field label="Firmen-E-Mail" colSpan={2}>
          <input type="email" value={form.email || ""} onChange={f("email")} data-testid="emp-field-email" className={inputCls} />
        </Field>
      </Group>

      <Group title="Adresse">
        <Field label="Straße & Hausnummer" required colSpan={2}>
          <input value={form.address || ""} onChange={f("address")} data-testid="emp-field-address"
            className={`${inputCls} ${!form.address ? "border-red-500/40" : ""}`} />
        </Field>
        <Field label="Land" required>
          <CountryPicker value={form.country || "AT"} onChange={(v) => setForm({ ...form, country: v })} testid="emp-field-country" />
        </Field>
        <Field label="PLZ">
          <input value={form.zip_code || ""} onChange={f("zip_code")} className={inputCls} />
        </Field>
        <Field label="Stadt" colSpan={2}>
          <CityPicker value={form.city || ""} country={form.country || "AT"} onChange={(v) => setForm({ ...form, city: v })} testid="emp-field-city" />
        </Field>
      </Group>

      <Group title="Bankverbindung">
        <Field label="IBAN" colSpan={2}>
          <input value={form.iban || ""} onChange={f("iban")} placeholder="AT…" className={inputCls + " font-mono"} />
        </Field>
        <Field label="BIC">
          <input value={form.bic || ""} onChange={f("bic")} className={inputCls + " font-mono"} />
        </Field>
        <Field label="Bank">
          <input value={form.bank_name || ""} onChange={f("bank_name")} placeholder="z.B. Erste Bank" className={inputCls} />
        </Field>
      </Group>

      <Group title="Notfallkontakt">
        <Field label="Name">
          <input value={form.emergency_contact_name || ""} onChange={f("emergency_contact_name")} data-testid="emp-field-emergency_name" className={inputCls} />
        </Field>
        <Field label="Telefon">
          <input value={form.emergency_contact_phone || ""} onChange={f("emergency_contact_phone")} data-testid="emp-field-emergency_phone" className={inputCls} />
        </Field>
        <Field label="Verhältnis" colSpan={2}>
          <input value={form.emergency_contact_relation || ""} onChange={f("emergency_contact_relation")} placeholder="z.B. Ehepartner, Eltern" data-testid="emp-field-emergency_relation" className={inputCls} />
        </Field>
      </Group>

      <Advanced testid="emp-step1-advanced-toggle">
        <Field label="Mitarbeiternummer">
          <input value={form.employee_number || ""} onChange={f("employee_number")} placeholder="z.B. M-2026-001" data-testid="emp-field-employee_number" className={inputCls} />
        </Field>
        <Field label="Geburtsort">
          <input value={form.birth_place || ""} onChange={f("birth_place")} placeholder="z.B. Wien" data-testid="emp-field-birth_place" className={inputCls} />
        </Field>
        <Field label="Krankenkasse">
          <input value={form.health_insurance || ""} onChange={f("health_insurance")} placeholder="z.B. ÖGK" data-testid="emp-field-health_insurance" className={inputCls} />
        </Field>
        <Field label="Adresse Zusatz">
          <input value={form.address_extra || ""} onChange={f("address_extra")} placeholder="Stiege / Tür" className={inputCls} />
        </Field>
      </Advanced>
    </div>
  );
}

// ─── Step 2: Anstellung & Lohn ────────────────────────────────────
function StepEmployment({ form, setForm, f, ruleGroups }) {
  return (
    <div>
      <Group title="Anstellung">
        <Field label="Anmeldedatum">
          <input type="date" value={form.employment_start || ""} onChange={f("employment_start")} data-testid="emp-field-emp_start" className={inputCls} />
        </Field>
        <Field label="Erster Arbeitstag">
          <input type="date" value={form.first_working_date || ""} onChange={f("first_working_date")} data-testid="emp-field-first_working_date" className={inputCls} />
        </Field>
        <Field label="Abteilung" required>
          <DepartmentPicker required value={form.department_id} onChange={(id) => setForm({ ...form, department_id: id })} testid="emp-department-picker"/>
        </Field>
        <Field label="Firma (übersteuert Abteilung)">
          <EmpFirmSelect value={form.firm_id || ""} onChange={(v) => setForm({ ...form, firm_id: v })} />
        </Field>
        <Field label="Carrier / DSP">
          <select value={form.carrier || ""} onChange={f("carrier")} data-testid="emp-field-carrier" className={inputCls}>
            <option value="">— keiner —</option>
            <option value="amazon">Amazon</option>
            <option value="ups">UPS</option>
            <option value="gls">GLS</option>
            <option value="dpd">DPD</option>
            <option value="dhl">DHL</option>
          </select>
        </Field>
        <Field label="Position">
          <select value={form.position || ""} onChange={f("position")} data-testid="emp-field-position" className={inputCls}>
            <option value="">— wählen —</option>
            <option value="zusteller">Zusteller</option>
            <option value="dispo">Dispo</option>
            <option value="buero">Büro</option>
            <option value="lager">Lager</option>
          </select>
        </Field>
        <Field label="Status">
          <select value={form.status} onChange={f("status")} data-testid="emp-field-status" className={inputCls}>
            {["aktiv", "urlaub", "krank", "inaktiv"].map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </Field>
        <Field label="Rolle">
          <select value={form.role} onChange={f("role")} data-testid="emp-field-role" className={inputCls}>
            {["admin", "geschaeftsfuehrung", "fuhrparkleiter", "disponent", "abteilungsleiter", "backoffice", "fahrer", "buchhalter", "viewer"].map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </Field>
      </Group>

      <Group title="Lohn & Arbeitszeit">
        <Field label="KV-Kategorie (Zusteller A–E)">
          <KVCategoryPicker value={form.kv_category || ""} onChange={(v) => setForm({ ...form, kv_category: v })} />
        </Field>
        <Field label="Arbeitszeit-Modell">
          <WorkHourModelPicker value={form.working_hour_model || ""} onChange={(v) => setForm({ ...form, working_hour_model: v })} />
        </Field>
        <Field label="Wochenstunden" required>
          <input type="number" step="0.5" min="0" max="60" value={form.weekly_hours || 40}
            onChange={(e) => setForm({ ...form, weekly_hours: parseFloat(e.target.value || 40), contracted_weekly_hours: parseFloat(e.target.value || 40) })}
            className={inputCls} />
        </Field>
        <Field label="Gehaltsmodell">
          <select value={form.salary_type || "hourly"} onChange={f("salary_type")} className={inputCls} data-testid="emp-salary-type">
            <option value="hourly">Stundenlohn</option>
            <option value="monthly">Monatsgehalt</option>
            <option value="parttime">Teilzeit</option>
            <option value="geringfuegig">Geringfügig</option>
          </select>
        </Field>
        <Field label="Regelgruppe (Überstd./Diäten)" colSpan={2}>
          <select value={form.payroll_rule_group_id || ""} onChange={f("payroll_rule_group_id")}
            data-testid="emp-payroll-rule-group" className={inputCls}>
            <option value="">— Standard (Allgemein) —</option>
            {ruleGroups.map((g) => (
              <option key={g.id} value={g.id}>{g.label}{g.is_system ? " · System" : ""}</option>
            ))}
          </select>
          <p className="text-[10px] text-zinc-500 mt-1">
            Konfigurierbar in <span className="text-zinc-300">Einstellungen → Lohnabrechnung</span>.
          </p>
        </Field>
      </Group>

      <Group title="Mindestanmeldung">
        <Field label="Protokollnummer" colSpan={2}>
          <div className="flex gap-2">
            <input value={form.protokollnummer || ""} onChange={f("protokollnummer")} data-testid="emp-field-protokollnummer" className={inputCls} placeholder="z.B. 12345-2026" />
            <MindestanmeldungInfo />
          </div>
        </Field>
      </Group>

      <Group title="Lohn-Berechtigungen">
        <div className="sm:col-span-2 grid grid-cols-2 sm:grid-cols-3 gap-2 bg-zinc-900/40 rounded p-3 border border-zinc-800">
          <BoolCheck label="Überstunden" testid="emp-overtime-eligible"
            checked={form.overtime_eligible !== false}
            onChange={(v) => setForm({ ...form, overtime_eligible: v })} />
          <BoolCheck label="Tagesgeld" testid="emp-tagesgeld-eligible"
            checked={form.tagesgeld_eligible !== false}
            onChange={(v) => setForm({ ...form, tagesgeld_eligible: v })} />
          <BoolCheck label="Sa-Zuschlag"
            checked={form.saturday_bonus_eligible !== false}
            onChange={(v) => setForm({ ...form, saturday_bonus_eligible: v })} />
          <BoolCheck label="So-Zuschlag"
            checked={form.sunday_bonus_eligible !== false}
            onChange={(v) => setForm({ ...form, sunday_bonus_eligible: v })} />
          <BoolCheck label="Feiertag-Zuschlag"
            checked={form.holiday_bonus_eligible !== false}
            onChange={(v) => setForm({ ...form, holiday_bonus_eligible: v })} />
        </div>
      </Group>

      <Advanced testid="emp-step2-advanced-toggle">
        <Field label="Abmeldedatum">
          <input type="date" value={form.employment_end || ""} onChange={f("employment_end")} data-testid="emp-field-emp_end" className={inputCls} />
        </Field>
        <Field label="Änderungsdatum">
          <input type="date" value={form.employment_change_date || ""} onChange={f("employment_change_date")} className={inputCls} />
        </Field>
        <Field label="Urlaubsanspruch ab">
          <input type="date" value={form.vacation_start_date || ""} onChange={f("vacation_start_date")} className={inputCls} />
        </Field>
        <Field label="Beitragskontonummer">
          <input value={form.contribution_account_number || ""} onChange={f("contribution_account_number")} className={inputCls} />
        </Field>
        <Field label="Tägl. Normalarbeitszeit (Std.)">
          <input type="number" step="0.25" min="0" max="24" value={form.daily_normal_hours || 8} onChange={f("daily_normal_hours")} className={inputCls} />
        </Field>
        <Field label="Standard-Einsatzort">
          <input value={form.default_workplace || ""} onChange={f("default_workplace")} data-testid="emp-field-workplace" className={inputCls} />
        </Field>
        <Field label="Lohnabrechnungs-Nr.">
          <input value={form.payroll_number || ""} onChange={f("payroll_number")} placeholder="z.B. P-2024-001" className={inputCls} />
        </Field>
        <Field label="Kostenstelle">
          <input value={form.cost_center || ""} onChange={f("cost_center")} data-testid="emp-field-cost_center" className={inputCls} />
        </Field>
        <Field label="Subunternehmen">
          <input value={form.subcontractor || ""} onChange={f("subcontractor")} className={inputCls} />
        </Field>
        <Field label="Zeiterfassung-Standort">
          <input value={form.time_tracking_workplace || ""} onChange={f("time_tracking_workplace")} className={inputCls} />
        </Field>
        <Field label="Timesheet-Nr.">
          <input value={form.timesheet_number || ""} onChange={f("timesheet_number")} className={inputCls} />
        </Field>
        <Field label="Continental / VDO ID">
          <input value={form.continental_vdo_id || ""} onChange={f("continental_vdo_id")} className={inputCls} />
        </Field>
        <Field label="Lenkprotokoll-Art">
          <select value={form.driving_log_type || ""} onChange={f("driving_log_type")} className={inputCls}>
            <option value="">— wählen —</option>
            <option value="stundenlohn">Stundenlohn</option>
            <option value="pauschal">Pauschal</option>
            <option value="km_basiert">Km-basiert</option>
            <option value="nach_fahrt">Nach Fahrt</option>
          </select>
        </Field>
        <Field label="Zertifikat">
          <input value={form.certificate || ""} onChange={f("certificate")} placeholder="z.B. C95" className={inputCls} />
        </Field>
        <Field label="Notizen" colSpan={2}>
          <textarea rows={2} value={form.notes || ""} onChange={f("notes")} className={inputCls} />
        </Field>
        <Field label="Lohn-Notizen" colSpan={2}>
          <textarea rows={2} value={form.payroll_notes || ""} onChange={f("payroll_notes")} placeholder="Sonderregelungen…" className={inputCls + " resize-none"} />
        </Field>
        <Field label="Optionen" colSpan={2}>
          <div className="grid grid-cols-2 gap-2 bg-zinc-950 rounded p-3">
            <BoolCheck label="Dokumente erstellen"
              checked={!!form.create_documents}
              onChange={(v) => setForm({ ...form, create_documents: v })} />
            <BoolCheck label="Überstunden nach Tagen"
              checked={!!form.overtime_calc_by_days}
              onChange={(v) => setForm({ ...form, overtime_calc_by_days: v })} />
            <BoolCheck label="Urlaub ab Anmeldedatum"
              checked={!!form.vacation_calc_from_start}
              onChange={(v) => setForm({ ...form, vacation_calc_from_start: v })} />
            <BoolCheck label="Samstag in Zeiterfassung"
              checked={!!form.saturday_in_tracking}
              onChange={(v) => setForm({ ...form, saturday_in_tracking: v })}
              testid="emp-field-saturday" />
          </div>
        </Field>
      </Advanced>
    </div>
  );
}

// ─── Step 3: Dokumente & Portal ──────────────────────────────────
function StepDocsPortal({ form, setForm, f, editing }) {
  return (
    <div>
      <Group title="Führerschein">
        <Field label="Klasse(n)">
          <input value={form.driver_license_groups || ""} onChange={f("driver_license_groups")} placeholder="B, B+E, C" data-testid="emp-field-license_groups" className={inputCls} />
        </Field>
        <Field label="Führerscheinnummer">
          <input value={form.driver_license_number || ""} onChange={f("driver_license_number")} placeholder="z.B. BXI 840032" data-testid="emp-field-license_number" className={inputCls} />
        </Field>
        <Field label="Ausstellungsbehörde">
          <input value={form.driver_license_authority || ""} onChange={f("driver_license_authority")} placeholder="z.B. TSC 4841" data-testid="emp-field-license_authority" className={inputCls} />
        </Field>
        <Field label="Ablaufdatum">
          <input type="date" value={form.driver_license_expiry || ""} onChange={f("driver_license_expiry")} data-testid="emp-field-license_expiry" className={inputCls} />
        </Field>
      </Group>

      <Group title="Identitätsdokument">
        <Field label="Art">
          <select value={form.identity_doc_type || ""} onChange={f("identity_doc_type")} data-testid="emp-field-identity_doc_type" className={inputCls}>
            <option value="reisepass">Reisepass</option>
            <option value="personalausweis">Personalausweis</option>
            <option value="fuehrerschein_id">Führerschein (als Ausweis)</option>
            <option value="andere">Andere</option>
          </select>
        </Field>
        <Field label="Nummer">
          <input value={form.identity_doc_number || ""} onChange={f("identity_doc_number")} data-testid="emp-field-identity_doc_number" className={inputCls} />
        </Field>
        <Field label="Ausgestellt am">
          <input type="date" value={form.identity_doc_issue_date || ""} onChange={f("identity_doc_issue_date")} className={inputCls} />
        </Field>
        <Field label="Gültig bis">
          <input type="date" value={form.identity_doc_valid_until || ""} onChange={f("identity_doc_valid_until")} data-testid="emp-field-identity_valid_until" className={inputCls} />
        </Field>
        <Field label="Behörde" colSpan={2}>
          <input value={form.identity_doc_authority || ""} onChange={f("identity_doc_authority")} placeholder="z.B. BH Wien" className={inputCls} />
        </Field>
      </Group>

      <Group title="Staatsbürgerschaft">
        <Field label="Land" colSpan={2}>
          <CountryPicker value={form.citizenship || ""} onChange={(v) => setForm({ ...form, citizenship: v })} testid="emp-field-citizenship" />
          <div className="mt-2">
            {needsResidenceDocs(form.citizenship) ? (
              <div data-testid="emp-citizenship-noneu-hint" className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/30 text-[11px] text-amber-300">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                Nicht-EU/EWR — Aufenthaltstitel &amp; Arbeitserlaubnis erforderlich
              </div>
            ) : form.citizenship ? (
              <div data-testid="emp-citizenship-eu-hint" className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-[11px] text-emerald-300">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                EU/EWR/Schweiz — kein Aufenthaltstitel nötig
              </div>
            ) : null}
          </div>
        </Field>
      </Group>

      {needsResidenceDocs(form.citizenship) && (
        <Group title="Aufenthaltstitel & Arbeitserlaubnis (Nicht-EU)">
          <Field label="Dokumentenart">
            <select value={form.residence_doc_type || ""} onChange={f("residence_doc_type")} data-testid="emp-field-residence_doc_type" className={inputCls}>
              <option value="">— wählen —</option>
              <option value="aufenthaltstitel">Aufenthaltstitel</option>
              <option value="rot_weiss_rot">Rot-Weiß-Rot Karte</option>
              <option value="rot_weiss_rot_plus">Rot-Weiß-Rot Karte plus</option>
              <option value="daueraufenthalt_eu">Daueraufenthalt EU</option>
              <option value="aufenthaltsbewilligung">Aufenthaltsbewilligung</option>
              <option value="visa">Visum (Typ C/D)</option>
              <option value="bestaetigung">Anmeldebescheinigung</option>
            </select>
          </Field>
          <Field label="Dokumentnummer">
            <input value={form.residence_doc_number || ""} onChange={f("residence_doc_number")} data-testid="emp-field-residence_doc_number" className={inputCls} />
          </Field>
          <Field label="Behörde">
            <input value={form.residence_authority || ""} onChange={f("residence_authority")} placeholder="z.B. MA 35 Wien" className={inputCls} />
          </Field>
          <Field label="Ausgestellt am">
            <input type="date" value={form.residence_issue_date || ""} onChange={f("residence_issue_date")} className={inputCls} />
          </Field>
          <Field label="Aufenthaltstitel gültig bis">
            <input type="date" value={form.residence_valid_until || ""} onChange={f("residence_valid_until")} data-testid="emp-field-residence_valid_until" className={inputCls} />
          </Field>
          <Field label="Arbeitserlaubnis-Art">
            <input value={form.work_permit || ""} onChange={f("work_permit")} placeholder="z.B. Beschäftigungsbewilligung" data-testid="emp-field-work_permit" className={inputCls} />
          </Field>
          <Field label="Arbeitserlaubnis gültig bis" colSpan={2}>
            <input type="date" value={form.work_permit_valid_until || ""} onChange={f("work_permit_valid_until")} data-testid="emp-field-work_permit_valid_until" className={inputCls} />
            <p className="text-[10px] text-zinc-500 mt-1">
              Beides löst 30/14/7 Tage vor Ablauf automatische Warnungen aus.
            </p>
          </Field>
        </Group>
      )}

      {!editing && form.role === "fahrer" && (
        <Group title="Fahrer-Portal-Zugang">
          <Field label="Portal-Zugang gleich aktivieren?" colSpan={2}>
            <label className="flex items-start gap-3 cursor-pointer p-3 rounded border border-zinc-800 bg-zinc-900/40 hover:bg-zinc-900/70" data-testid="emp-portal-enable-toggle">
              <input
                type="checkbox"
                checked={!!form._enable_portal_on_create}
                onChange={(e) => setForm({ ...form, _enable_portal_on_create: e.target.checked })}
                className="w-4 h-4 mt-0.5 rounded accent-amber-500"
              />
              <div>
                <span className="text-sm text-zinc-100 font-medium">
                  Ja, Login-Daten beim Anlegen automatisch erzeugen
                </span>
                <p className="text-[11px] text-zinc-500 mt-0.5">
                  Benutzername &amp; Initial-Passwort werden <span className="text-amber-400 font-medium">einmalig</span> angezeigt — bitte direkt kopieren und dem Fahrer übergeben.
                </p>
              </div>
            </label>
          </Field>
          {form._enable_portal_on_create && (
            <Field label="Benutzername (optional)" colSpan={2}>
              <input
                value={form._portal_username || ""}
                onChange={f("_portal_username")}
                data-testid="emp-portal-username"
                placeholder={
                  form.first_name && form.last_name
                    ? `Auto: ${form.first_name.trim().toLowerCase()}.${form.last_name.trim().toLowerCase()}`
                    : "Auto: vorname.nachname"
                }
                className={inputCls + " font-mono"}
              />
              <p className="text-[11px] text-zinc-500 mt-1">
                Leer = automatisch aus Vor-/Nachname.
              </p>
            </Field>
          )}
        </Group>
      )}
    </div>
  );
}

// ─── Small bool checkbox ─────────────────────────────────────────
function BoolCheck({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer text-xs text-zinc-200">
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        data-testid={testid}
        className="w-4 h-4 rounded accent-emerald-600"
      />
      {label}
    </label>
  );
}

// ─── Helper components (unchanged) ───────────────────────────────
function EmpFirmSelect({ value, onChange }) {
  const [firms, setFirms] = useState([]);
  useEffect(() => {
    api.get("/firms").then(({ data }) => setFirms(data || [])).catch(() => setFirms([]));
  }, []);
  return (
    <select
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      data-testid="emp-firm-select"
      className={inputCls}
    >
      <option value="">— Auto (Firma der Abteilung) —</option>
      {firms.map((f) => (
        <option key={f.id} value={f.id}>{f.name}</option>
      ))}
    </select>
  );
}

function KVCategoryPicker({ value, onChange }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/payroll-settings").then((r) => {
      setItems(r.data?.sections?.kv_categories || []);
    }).catch(() => setItems([]));
  }, []);
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid="emp-field-kv-category">
      <option value="">— wählen —</option>
      {items.map((c) => (
        <option key={c.key} value={c.key}>
          {c.key} · {c.label}{c.monthly_brutto ? ` (${c.monthly_brutto}€)` : ""}
        </option>
      ))}
    </select>
  );
}

function WorkHourModelPicker({ value, onChange }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/payroll-settings").then((r) => {
      setItems(r.data?.sections?.work_hour_models || []);
    }).catch(() => setItems([]));
  }, []);
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid="emp-field-work-hour-model">
      <option value="">— wählen —</option>
      {items.map((m) => (
        <option key={m.key} value={m.key}>
          {m.label} · {m.hours_per_day}h/Tag
        </option>
      ))}
    </select>
  );
}

function MindestanmeldungInfo() {
  const [open, setOpen] = useState(false);
  const [firm, setFirm] = useState(null);
  useEffect(() => {
    if (!open) return;
    api.get("/firms").then((r) => {
      const list = r.data?.items || r.data || [];
      setFirm(list.find((f) => f.is_default) || list[0]);
    }).catch(() => setFirm(null));
  }, [open]);
  return (
    <div className="relative">
      <button type="button" onClick={() => setOpen(!open)} className="px-3 py-2 rounded border border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/20 text-amber-200 text-xs whitespace-nowrap" data-testid="mindestanmeldung-info-btn">
        ℹ Anleitung
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 z-50 w-[420px] rounded-lg border border-zinc-700 bg-zinc-900 shadow-xl p-4 text-xs text-zinc-200 space-y-2" data-testid="mindestanmeldung-info-panel">
          <div className="flex justify-between items-start">
            <h4 className="font-display font-semibold text-amber-200">Mindestanmeldung — Telefonisch</h4>
            <button onClick={() => setOpen(false)} className="text-zinc-500 hover:text-zinc-200">×</button>
          </div>
          <p className="text-zinc-400">Für die Mindestanmeldung der Krankenkasse durchgeben:</p>
          <ol className="list-decimal list-inside space-y-1 pl-2">
            <li><b>Beitragskontonummer</b>: <span className="font-mono text-emerald-300">{firm?.beitragskontonummer || "— nicht hinterlegt —"}</span></li>
            <li><b>Firmenname</b>: <span className="text-emerald-300">{firm?.legal_name || firm?.name || "—"}</span></li>
            <li><b>Vor- &amp; Nachname</b> des Fahrers</li>
            <li><b>SV-Nummer</b> (oder Geburtsdatum falls keine)</li>
          </ol>
          <p className="text-zinc-400 pt-2 border-t border-zinc-800">
            Die erhaltene <b>Protokollnummer</b> oben eintragen.
          </p>
          <div className="rounded bg-zinc-950 p-2 mt-2 text-[11px]">
            <span className="text-zinc-500">Krankenkasse:</span>{" "}
            <span className="text-zinc-200">{firm?.krankenkasse || "ÖGK"}</span>
          </div>
        </div>
      )}
    </div>
  );
}
